import React from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  container: {
    width: 324,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  blurred: {
    filter: 'blur(3px)',
    '-webkit-filter': 'blur(3px)',
    '-moz-filter': 'blur(3px)',
    '-o-filter': 'blur(3px)',
    '-ms-filter': 'blur(3px)',
  },
  label: {
    fontFamily: 'MadaBold',
    fontSize: 14,
  },
  date: {
    fontFamily: 'MadaRegular',
    color: '#4B4B4B',
    fontSize: 14,
  },
}));

const VERTICAL_LINES = [
  [60],
  [105, 235],
  [70, 170, 270],
  [50, 130, 210, 290],
  [40, 105, 170, 235, 300],
];

export default function TrendsChart({
  blurred,
  values,
  sortedIntervals = [],
  scale,
  useScaleAsMax,
  minMaxOnAxis,
}) {
  const classes = useStyles();
  const verticalLinesIndex = values.length - 1;

  const max = useScaleAsMax ? scale : 100;

  const viewBox = minMaxOnAxis ? '0 0 365 204' : '0 0 340 204';
  const baseline = minMaxOnAxis ? 25 : 0;
  const verticalBarStrokeWidth = minMaxOnAxis ? 4 : 8;

  const minMaxValues = minMaxOnAxis ? [0, max] : [];

  return (
    <div className={_classNames(classes.container, blurred && classes.blurred)}>
      <svg
        height="auto"
        width="100%"
        viewBox={viewBox}
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x={baseline} y="14" width="340" height="160" fill="#F8F9F9" />
        <line
          strokeWidth={1}
          stroke="#D8D8D8"
          strokeLinecap="square"
          x1={baseline}
          x2={baseline + 340}
          y1="174"
          y2="174"
        />
        {values.map(({ score }, i) => {
          // Draw vertical guide lines
          const verticalLineX =
            baseline + VERTICAL_LINES[verticalLinesIndex][i];
          const textY = 14 + (1 - score / max) * 160;

          const connectorX1 =
            baseline +
            (i === 0 ? 0 : VERTICAL_LINES[verticalLinesIndex][i - 1]);
          const connectorY1 =
            i === 0 ? 174 : 14 + (1 - values[i - 1].score / max) * 160;

          return (
            <>
              <line
                strokeWidth={1}
                stroke="#D8D8D8"
                strokeLinecap="square"
                x1={verticalLineX}
                x2={verticalLineX}
                y1="14"
                y2="174"
              />
              <line
                strokeWidth={1}
                stroke="#D8D8D8"
                strokeLinecap="square"
                x1={connectorX1}
                x2={verticalLineX}
                y1={connectorY1}
                y2={textY}
              />
            </>
          );
        })}
        {values.map(({ score, date, color }, i) => {
          // Draw data points (connnector line, point and date on X-axis)
          const verticalLineX =
            baseline + VERTICAL_LINES[verticalLinesIndex][i];
          const textY = 14 + (1 - score / max) * 160;

          return (
            <>
              <rect
                x={verticalLineX - 20}
                y={textY - 13}
                width="40"
                height="26"
                stroke={color}
                strokeWidth="1px"
                fill="#FFFFFF"
                rx="13px"
                ry="13px"
                strokeLinejoin="round"
              />
              <text
                x={verticalLineX}
                y={textY + 4}
                textAnchor="middle"
                className={classes.label}
                color="#4B4B4B"
              >
                {Math.round(score)}
              </text>
              <text
                x={verticalLineX}
                y="199"
                textAnchor="middle"
                className={classes.date}
              >
                {date}
              </text>
            </>
          );
        })}
        {sortedIntervals.map(interval => {
          // Draw vertical bars on the left
          const startY = 14 + 160 * (1 - interval.start / scale);
          const endY = 14 + 160 * (1 - interval.end / scale);

          return (
            <line
              strokeWidth={verticalBarStrokeWidth}
              stroke={interval.color}
              strokeLinecap="butt"
              x1={baseline}
              x2={baseline}
              y1={startY}
              y2={endY}
            />
          );
        })}
        {minMaxValues.map(value => {
          const textY = 14 + (1 - value / max) * 160;

          return (
            <text
              x={baseline - 6}
              y={textY + 4}
              textAnchor="end"
              className={classes.date}
              color="#4B4B4B"
            >
              {value}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

TrendsChart.propTypes = {
  blurred: PropTypes.bool,
  values: PropTypes.array,
  sortedIntervals: PropTypes.array,
  scale: PropTypes.number,
  useScaleAsMax: PropTypes.bool,
  minMaxOnAxis: PropTypes.bool,
};

TrendsChart.defaultProps = {
  scale: 100,
};
